/**
 * Admitted-Students Analytics API Service (RTK Query)
 *
 * Injects the `/admission/admitted/*` endpoints into the shared `baseApi`,
 * mirroring `admissionApi.ts`'s pattern. Unlike that file (which reports on
 * every *applicant*), everything here reports on actually **admitted**
 * students, and only exists for Balvatika and Class 1 — see `ClassGroup` in
 * `../types` and `doc/ADMISSION_ANALYTICS_API.md` for the full backend
 * contract and the real-data findings that shape it (distance is a coded
 * band, not a km number; the backend dedupes heavy duplicate rows itself).
 *
 * Every endpoint requires `academicYear` (format `"YYYY-YY"`, e.g.
 * `"2026-27"` — reuses `getCurrentAcademicYearParam()` from `admissionApi.ts`
 * as the single source of truth for "which cycle") and accepts an optional
 * `classGroup` (`'BV' | 'C1' | 'ALL'`, default `'ALL'`).
 *
 * Distribution buckets returned here (`RawDistributionItem`) carry only
 * `{code, count, percentage}` — no label. Labels are resolved client-side via
 * `translate()` + the dimension's Code_Label_Map, exactly like every other
 * distribution in this module, so the API layer stays label-agnostic.
 */

import { baseApi } from '../../../../store/api/baseApi';
import type { AdmissionEntityRow, GenderMetric, ClassGroup } from '../types';
import { getCurrentAcademicYearParam } from './admissionApi';

const REQUEST_TIMEOUT_MS = 30_000;
const BASE_URL = '/admission/admitted';

// ─── Response types ───────────────────────────────────────────

export interface RawDistributionItem {
  code: string | null;
  count: number;
  percentage: number | null;
}

export interface AdmittedSummary {
  academicYear: string;
  classGroup: ClassGroup;
  total: number;
  genderCounts: GenderMetric[];
  differentlyAbledYes: number;
  rteYes: number;
  entities: { kvs: number; regions: number };
}

export interface AdmittedGeoLevelResponse {
  summary: { total: number; entities: number };
  items: AdmissionEntityRow[];
}

export interface AdmittedDetailSummary {
  total: number;
  genderCounts: GenderMetric[];
  differentlyAbledYes: number;
  rteYes: number;
}

export interface AdmittedRegionDetail {
  dataAvailable: boolean;
  summary: AdmittedDetailSummary;
  distributions: Record<string, RawDistributionItem[]>;
  /** Which states this region's KVs sit in — a cross-tab, not a hierarchy. */
  states: AdmissionEntityRow[];
  /** The region's own schools. `code` is the kv_id, used to open its detail
   * page. KVs are the one entity a region actually contains. */
  kvs: AdmissionEntityRow[];
}

export interface AdmittedKvMeta {
  kvCode: string;
  kvName: string | null;
  region: string | null;
  state: string | null;
  district: string | null;
}

export interface AdmittedKvDetail {
  dataAvailable: boolean;
  meta: AdmittedKvMeta;
  summary: AdmittedDetailSummary;
  distributions: Record<string, RawDistributionItem[]>;
}

export interface AdmittedRteSummary {
  total: number;
  eligible: number;
  nonEligible: number;
  eligiblePct: number;
}

export interface AdmittedRteBreakdown {
  bySocialCategory: RawDistributionItem[];
  byParentalIncome: RawDistributionItem[];
  byDistance: RawDistributionItem[];
  byGender: RawDistributionItem[];
}

/**
 * One parent's employment/income profile. `incomeGovt` and `incomeNonGovt` are
 * deliberately separate: the raw `income_group` code space depends on whether
 * the parent is a government employee — government parents use the 6-value
 * ANNUAL_INCOME scale (0-5), non-government the 7-value NON_GOVT_ANNUAL_INCOME
 * scale (0-6). The same code denotes a different rupee band on each scale, so
 * they are translated with different maps and never merged.
 * `employeeGroup` (Group A-D) is government-service-only.
 */
export interface AdmittedParentIncomeBlock {
  govtEmployee: RawDistributionItem[];
  incomeGovt: RawDistributionItem[];
  incomeNonGovt: RawDistributionItem[];
  employeeGroup: RawDistributionItem[];
  qualification: RawDistributionItem[];
}

export interface AdmittedIncomeProfile {
  summary: {
    total: number;
    singleParent: number;
    /** Single-parent families where the father is the sole parent — these have
     * no mother_* data at all (not "unreported"). */
    singleFather: number;
    /** Single-parent families where the mother is the sole parent — no father_*
     * data at all. */
    singleMother: number;
    fatherGovt: number;
    motherGovt: number;
  };
  father: AdmittedParentIncomeBlock;
  mother: AdmittedParentIncomeBlock;
}

export interface AdmittedRteRegionRate {
  name: string;
  total: number;
  eligible: number;
  ratePct: number;
}

export interface AdmittedDistanceSummary {
  total: number;
  bands: RawDistributionItem[];
}

export interface AdmittedDistanceGeoItem {
  code: string;
  name: string;
  total: number;
  within: number;
  beyond: number;
  other: number;
  withinPct: number;
}

export interface AdmittedRecordItem {
  admissionId: string;
  classCode: string | null;
  kvCode: string;
  kvName: string | null;
  region: string | null;
  state: string | null;
  gender: string | null;
  socialCategory: string | null;
  differentlyAbled: string;
  disabilityCategory?: string;
  rteEligibility: string;
  incomeGroup?: string;
  sponsoredBy?: string;
  locationType?: string;
  distanceFromSchool?: string;
  serviceCategory?: string;
}

export interface AdmittedRecordsResponse {
  items: AdmittedRecordItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export type AdmittedFilterOptions = Record<string, Array<{ code: string }>>;

export type AdmittedDistributions = Record<string, RawDistributionItem[]>;

export type GeoLevel = 'region' | 'state' | 'kv';

// ─── Shared query args ────────────────────────────────────────

interface BaseArgs {
  academicYear?: string;
  classGroup?: ClassGroup;
}

/**
 * The combinational filter's query args — multi-value, OR'd within each
 * dimension and AND'd across them. Mirrors the backend's `DimensionFilters`
 * key-for-key (and `FilterState` on the UI side); every endpoint that accepts
 * these applies them uniformly, which is what lets one filter drive every view
 * (e.g. RTE-eligible + Social Category = SC + govt father).
 *
 * `fatherIncomeBand`/`motherIncomeBand` hold **band keys** (see `INCOME_BAND`),
 * not raw income codes: government and non-government parents store the same
 * rupee band under different codes, so the backend expands a band to the correct
 * code on each scale. Matching a raw code here would conflate two different
 * bands. Every other key holds raw codes.
 */
export interface DimensionFilterArgs {
  class?: string[];
  gender?: string[];
  socialCategory?: string[];
  /** Social *group* keys (see `SOCIAL_GROUP`), not raw social_category codes —
   * General vs General-EWS share code '11' and split on low_income_group. */
  socialGroup?: string[];
  sponsoredBy?: string[];
  locationType?: string[];
  serviceCategory?: string[];
  kvsWard?: string[];
  rte?: string[];
  distanceFromSchool?: string[];
  cwsn?: string[];
  disabilityCategory?: string[];
  parentalIncome?: string[];
  fatherGovtEmployee?: string[];
  motherGovtEmployee?: string[];
  singleParent?: string[];
  singleParentType?: string[];
  /** Rupee *band* keys (see `INCOME_BAND` in utils/constants), not raw income
   * codes — the backend maps a band to the right code per employment scale. */
  fatherIncomeBand?: string[];
  motherIncomeBand?: string[];
  fatherQualification?: string[];
  motherQualification?: string[];
  bloodGroup?: string[];
  fatherEmployeeGroup?: string[];
  motherEmployeeGroup?: string[];
  aadharSubmitted?: string[];
  hasSibling?: string[];
  /** {parent}_occupation dual-scale: occupation = non-govt (NON_GOVT_OCCUPATION
   * labels), designation = govt (GOVERNMENT_TYPE labels). */
  fatherOccupation?: string[];
  motherOccupation?: string[];
  fatherDesignation?: string[];
  motherDesignation?: string[];
}

interface BreakdownArgs extends BaseArgs {
  category?: string;
  subcategory?: string;
}

interface GeoLevelArgs extends BreakdownArgs, DimensionFilterArgs {
  region?: string;
  state?: string;
}

// ─── Transport helper ─────────────────────────────────────────

/**
 * Every endpoint shares the same envelope handling: build the query string
 * (dropping empty/undefined params, comma-joining array values), issue the
 * request, unwrap `{success, data}` or map a failure to a `CUSTOM_ERROR`.
 */
async function fetchAdmitted<T>(
  fetchWithBQ: any,
  path: string,
  args: Record<string, string | number | string[] | undefined>,
): Promise<{ data: T } | { error: any }> {
  const { academicYear, classGroup, ...rest } = args;
  const params: Record<string, string | number | string[] | undefined> = {
    academicYear: (academicYear as string | undefined) ?? getCurrentAcademicYearParam(),
    classGroup: (classGroup as string | undefined) ?? 'ALL',
    ...rest,
  };

  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(Array.isArray(v) ? v.join(',') : String(v))}`)
    .join('&');

  const result = await fetchWithBQ({
    url: `${BASE_URL}${path}?${query}`,
    method: 'GET',
    timeout: REQUEST_TIMEOUT_MS,
  });

  if (result.error) {
    return { error: result.error };
  }

  const body = result.data as { success: boolean; data?: T; message?: string };
  if (!body?.success) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: body?.message ?? 'Failed to load admitted-students data',
      },
    };
  }

  return { data: body.data as T };
}

/** Attach `kvCode` (from the raw `code`) on KV-level rows so existing
 * navigation logic (`row.kvCode`) keeps working unchanged. */
function withKvCode(level: GeoLevel, response: AdmittedGeoLevelResponse): AdmittedGeoLevelResponse {
  if (level !== 'kv') return response;
  return {
    ...response,
    items: response.items.map((item) => ({ ...item, kvCode: item.code, kvName: item.name })),
  };
}

export const admittedAnalyticsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAdmittedSummary: build.query<AdmittedSummary, (BaseArgs & DimensionFilterArgs) | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedSummary>(fetchWithBQ, '/summary', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedDistribution: build.query<RawDistributionItem[], BaseArgs & { category: string }>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<RawDistributionItem[]>(fetchWithBQ, '/distribution', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedDistributions: build.query<AdmittedDistributions, (BaseArgs & DimensionFilterArgs) | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedDistributions>(fetchWithBQ, '/distributions', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedGeoRegions: build.query<AdmittedGeoLevelResponse, GeoLevelArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        const res = await fetchAdmitted<AdmittedGeoLevelResponse>(fetchWithBQ, '/geo/regions', { ...arg });
        return 'data' in res ? { data: withKvCode('region', res.data) } : res;
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedGeoStates: build.query<AdmittedGeoLevelResponse, GeoLevelArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        const res = await fetchAdmitted<AdmittedGeoLevelResponse>(fetchWithBQ, '/geo/states', { ...arg });
        return 'data' in res ? { data: withKvCode('state', res.data) } : res;
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedGeoKvs: build.query<AdmittedGeoLevelResponse, GeoLevelArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        const res = await fetchAdmitted<AdmittedGeoLevelResponse>(fetchWithBQ, '/geo/kvs', { ...arg });
        return 'data' in res ? { data: withKvCode('kv', res.data) } : res;
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedRegionDetail: build.query<AdmittedRegionDetail, BaseArgs & DimensionFilterArgs & { region: string }>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedRegionDetail>(fetchWithBQ, '/geo/region-detail', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedKvDetail: build.query<AdmittedKvDetail, BaseArgs & DimensionFilterArgs & { kvCode: string }>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedKvDetail>(fetchWithBQ, '/geo/kv-detail', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedRteSummary: build.query<AdmittedRteSummary, BaseArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedRteSummary>(fetchWithBQ, '/rte/summary', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedRteBreakdown: build.query<AdmittedRteBreakdown, BaseArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedRteBreakdown>(fetchWithBQ, '/rte/breakdown', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedRteByRegion: build.query<AdmittedRteRegionRate[], BaseArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedRteRegionRate[]>(fetchWithBQ, '/rte/by-region', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedIncomeProfile: build.query<AdmittedIncomeProfile, (BaseArgs & DimensionFilterArgs) | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedIncomeProfile>(fetchWithBQ, '/income/profile', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedDistanceSummary: build.query<AdmittedDistanceSummary, BaseArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedDistanceSummary>(fetchWithBQ, '/distance/summary', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedDistanceByGeography: build.query<AdmittedDistanceGeoItem[], BaseArgs & { level?: GeoLevel }>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedDistanceGeoItem[]>(fetchWithBQ, '/distance/by-geography', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),

    getAdmittedRecords: build.query<
      AdmittedRecordsResponse,
      BaseArgs & DimensionFilterArgs & { region?: string; state?: string; kvCode?: string; page?: number; limit?: number }
    >({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedRecordsResponse>(fetchWithBQ, '/records', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 60,
    }),

    getAdmittedFilterOptions: build.query<AdmittedFilterOptions, BaseArgs | void>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        return fetchAdmitted<AdmittedFilterOptions>(fetchWithBQ, '/filter-options', { ...arg });
      },
      providesTags: ['Admission'],
      keepUnusedDataFor: 300,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdmittedSummaryQuery,
  useGetAdmittedDistributionQuery,
  useGetAdmittedDistributionsQuery,
  useGetAdmittedGeoRegionsQuery,
  useGetAdmittedGeoStatesQuery,
  useGetAdmittedGeoKvsQuery,
  useGetAdmittedRegionDetailQuery,
  useGetAdmittedKvDetailQuery,
  useGetAdmittedRteSummaryQuery,
  useGetAdmittedRteBreakdownQuery,
  useGetAdmittedRteByRegionQuery,
  useGetAdmittedIncomeProfileQuery,
  useGetAdmittedDistanceSummaryQuery,
  useGetAdmittedDistanceByGeographyQuery,
  useGetAdmittedRecordsQuery,
  useGetAdmittedFilterOptionsQuery,
} = admittedAnalyticsApi;
